import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { toErrorMessage } from 'vscode/vscode/vs/base/common/errorMessage';
import { Disposable, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { SimpleIconLabel } from 'vscode/vscode/vs/base/browser/ui/iconLabel/simpleIconLabel';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import { ShowTooltipCommand, StatusbarEntryKinds } from 'vscode/vscode/vs/workbench/services/statusbar/browser/statusbar';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { isThemeColor } from 'vscode/vscode/vs/editor/common/editorCommon';
import { show, hide, addDisposableListener, EventType, EventHelper, append } from 'vscode/vscode/vs/base/browser/dom';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { assertIsDefined } from 'vscode/vscode/vs/base/common/types';
import { StandardKeyboardEvent } from 'vscode/vscode/vs/base/browser/keyboardEvent';
import { renderIcon, renderLabelWithIcons } from 'vscode/vscode/vs/base/browser/ui/iconLabel/iconLabels';
import { syncing, spinningLoading } from 'vscode/vscode/vs/platform/theme/common/iconRegistry';
import { isMarkdownString, markdownStringEqual } from 'vscode/vscode/vs/base/common/htmlContent';
import { Gesture, EventType as EventType$1 } from 'vscode/vscode/vs/base/browser/touch';
import { IHoverService } from 'vscode/vscode/vs/platform/hover/browser/hover.service';

let StatusbarEntryItem = class StatusbarEntryItem extends Disposable {
    get name() {
        return assertIsDefined(this.entry).name;
    }
    get hasCommand() {
        return typeof this.entry?.command !== 'undefined';
    }
    constructor(container, entry, hoverDelegate, commandService, hoverService, notificationService, telemetryService, themeService) {
        super();
        this.container = container;
        this.hoverDelegate = hoverDelegate;
        this.commandService = commandService;
        this.hoverService = hoverService;
        this.notificationService = notificationService;
        this.telemetryService = telemetryService;
        this.themeService = themeService;
        this.entry = undefined;
        this.foregroundListener = this._register(( new MutableDisposable()));
        this.backgroundListener = this._register(( new MutableDisposable()));
        this.commandMouseListener = this._register(( new MutableDisposable()));
        this.commandTouchListener = this._register(( new MutableDisposable()));
        this.commandKeyboardListener = this._register(( new MutableDisposable()));
        this.focusListener = this._register(( new MutableDisposable()));
        this.focusOutListener = this._register(( new MutableDisposable()));
        this.hover = undefined;
        this.labelContainer = document.createElement('a');
        this.labelContainer.tabIndex = -1;
        this.labelContainer.setAttribute('role', 'button');
        this.labelContainer.className = 'statusbar-item-label';
        this._register(Gesture.addTarget(this.labelContainer));
        this.label = this._register(( new StatusBarCodiconLabel(this.labelContainer)));
        this.container.appendChild(this.labelContainer);
        this.beakContainer = document.createElement('div');
        this.beakContainer.className = 'status-bar-item-beak-container';
        this.container.appendChild(this.beakContainer);
        this.update(entry);
    }
    update(entry) {
        this.label.showProgress = entry.showProgress ?? false;
        if (!this.entry || entry.text !== this.entry.text) {
            this.label.text = entry.text;
            if (entry.text) {
                show(this.labelContainer);
            }
            else {
                hide(this.labelContainer);
            }
        }
        if (!this.entry || entry.ariaLabel !== this.entry.ariaLabel) {
            this.container.setAttribute('aria-label', entry.ariaLabel);
            this.labelContainer.setAttribute('aria-label', entry.ariaLabel);
        }
        if (!this.entry || entry.role !== this.entry.role) {
            this.labelContainer.setAttribute('role', entry.role || 'button');
        }
        if (!this.entry || !this.isEqualTooltip(this.entry, entry)) {
            const hoverContents = isMarkdownString(entry.tooltip) ? { markdown: entry.tooltip, markdownNotSupportedFallback: undefined } : entry.tooltip;
            if (this.hover) {
                this.hover.update(hoverContents);
            }
            else {
                this.hover = this._register(this.hoverService.setupUpdatableHover(this.hoverDelegate, this.container, hoverContents));
            }
            if (entry.command !== ShowTooltipCommand ) {
                this.focusListener.value = addDisposableListener(this.labelContainer, EventType.FOCUS, e => {
                    EventHelper.stop(e);
                    this.hover?.show(false);
                });
                this.focusOutListener.value = addDisposableListener(this.labelContainer, EventType.FOCUS_OUT, e => {
                    EventHelper.stop(e);
                    this.hover?.hide();
                });
            }
        }
        if (!this.entry || entry.command !== this.entry.command) {
            this.commandMouseListener.clear();
            this.commandTouchListener.clear();
            this.commandKeyboardListener.clear();
            const command = entry.command;
            if (command && (command !== ShowTooltipCommand || this.hover) ) {
                this.commandMouseListener.value = addDisposableListener(this.labelContainer, EventType.CLICK, () => this.executeCommand(command));
                this.commandTouchListener.value = addDisposableListener(this.labelContainer, EventType$1.Tap, () => this.executeCommand(command));
                this.commandKeyboardListener.value = addDisposableListener(this.labelContainer, EventType.KEY_DOWN, e => {
                    const event = ( new StandardKeyboardEvent(e));
                    if (event.equals(10 ) || event.equals(3 )) {
                        EventHelper.stop(e);
                        this.executeCommand(command);
                    }
                    else if (event.equals(9 ) || event.equals(15 ) || event.equals(17 )) {
                        EventHelper.stop(e);
                        this.hover?.hide();
                    }
                });
                this.labelContainer.classList.remove('disabled');
            }
            else {
                this.labelContainer.classList.add('disabled');
            }
        }
        if (!this.entry || entry.showBeak !== this.entry.showBeak) {
            if (entry.showBeak) {
                this.container.classList.add('has-beak');
            }
            else {
                this.container.classList.remove('has-beak');
            }
        }
        const hasBackgroundColor = !!entry.backgroundColor || (entry.kind && entry.kind !== 'standard');
        if (!this.entry || entry.kind !== this.entry.kind) {
            for (const kind of StatusbarEntryKinds) {
                this.container.classList.remove(`${kind}-kind`);
            }
            if (entry.kind && entry.kind !== 'standard') {
                this.container.classList.add(`${entry.kind}-kind`);
            }
            this.container.classList.toggle('has-background-color', hasBackgroundColor);
        }
        if (!this.entry || entry.color !== this.entry.color) {
            this.applyColor(this.labelContainer, entry.color);
        }
        if (!this.entry || entry.backgroundColor !== this.entry.backgroundColor) {
            this.container.classList.toggle('has-background-color', hasBackgroundColor);
            this.applyColor(this.container, entry.backgroundColor, true);
        }
        this.entry = entry;
    }
    isEqualTooltip({ tooltip }, { tooltip: otherTooltip }) {
        if (tooltip === undefined) {
            return otherTooltip === undefined;
        }
        if (isMarkdownString(tooltip)) {
            return isMarkdownString(otherTooltip) && markdownStringEqual(tooltip, otherTooltip);
        }
        return tooltip === otherTooltip;
    }
    async executeCommand(command) {
        if (command === ShowTooltipCommand) {
            this.hover?.show(true );
        }
        else {
            const id = typeof command === 'string' ? command : command.id;
            const args = typeof command === 'string' ? [] : command.arguments ?? [];
            this.telemetryService.publicLog2('workbenchActionExecuted', { id, from: 'status bar' });
            try {
                await this.commandService.executeCommand(id, ...args);
            }
            catch (error) {
                this.notificationService.error(toErrorMessage(error));
            }
        }
    }
    applyColor(container, color, isBackground) {
        let colorResult = undefined;
        if (isBackground) {
            this.backgroundListener.clear();
        }
        else {
            this.foregroundListener.clear();
        }
        if (color) {
            if (isThemeColor(color)) {
                colorResult = this.themeService.getColorTheme().getColor(color.id)?.toString();
                const listener = this.themeService.onDidColorThemeChange(theme => {
                    const colorValue = theme.getColor(color.id)?.toString();
                    if (isBackground) {
                        container.style.backgroundColor = colorValue ?? '';
                    }
                    else {
                        container.style.color = colorValue ?? '';
                    }
                });
                if (isBackground) {
                    this.backgroundListener.value = listener;
                }
                else {
                    this.foregroundListener.value = listener;
                }
            }
            else {
                colorResult = color;
            }
        }
        if (isBackground) {
            container.style.backgroundColor = colorResult ?? '';
        }
        else {
            container.style.color = colorResult ?? '';
        }
    }
};
StatusbarEntryItem = ( __decorate([
    ( __param(3, ICommandService)),
    ( __param(4, IHoverService)),
    ( __param(5, INotificationService)),
    ( __param(6, ITelemetryService)),
    ( __param(7, IThemeService))
], StatusbarEntryItem));
class StatusBarCodiconLabel extends SimpleIconLabel {
    constructor(container) {
        super(container);
        this.container = container;
        this.progressCodicon = renderIcon(syncing);
        this.currentText = '';
        this.currentShowProgress = false;
    }
    set showProgress(showProgress) {
        if (this.currentShowProgress !== showProgress) {
            this.currentShowProgress = showProgress;
            this.progressCodicon = renderIcon(showProgress === 'loading' ? spinningLoading : syncing);
            this.text = this.currentText;
        }
    }
    set text(text) {
        if (this.currentShowProgress) {
            if (this.container.firstChild !== this.progressCodicon) {
                this.container.appendChild(this.progressCodicon);
            }
            for (const node of Array.from(this.container.childNodes)) {
                if (node !== this.progressCodicon) {
                    node.remove();
                }
            }
            let textContent = text ?? '';
            if (textContent) {
                textContent = ` ${textContent}`;
            }
            append(this.container, ...renderLabelWithIcons(textContent));
        }
        else {
            super.text = text;
        }
    }
}

export { StatusbarEntryItem };
